import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

// Der David Media Installer: ein Download, alle Werkzeuge, Schluessel eintragen,
// Updates von selbst. Bis September 2026 war er auf der Website NIRGENDS
// verlinkt — nur wer ihn schon hatte, bekam Updates; ein Neukunde fand ihn nicht.
//
// Die Adressen kommen aus /downloads/installer/version.json, das
// GraphFlow-Installer/veroeffentlichen.sh bei jedem Release schreibt. Deshalb
// steht hier keine Versionsnummer und kein Link im Code.
//
// Der Installer ist der Launcher fuer ALLES — Einzelkauf, Studio-Paket, Abo,
// Gratis: Login mit dem Konto, drin ist, was man besitzt. Deshalb steht er
// ueberall, ohne Produkt-Bedingung (seit 1.5.11 sind auch Instant Grade und Media
// Flow im Bundle).

let _cache = null;
export function useInstaller() {
  const [info, setInfo] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    // Stundengenauer Cache-Brecher: nach einem Release sieht die Seite den
    // neuen Stand spaetestens nach einer Stunde, ohne bei jedem Aufruf zu laden.
    const v = Math.floor(Date.now() / 3600000);
    fetch(`/downloads/installer/version.json?v=${v}`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j && j.url) { _cache = j; setInfo(j); } })
      .catch(() => {});
  }, []);
  return info;
}

export const WINDOWS_HINWEIS = {
  de: <>Windows zeigt beim ersten Start „Der Computer wurde durch Windows geschützt“ — auf <strong>Weitere Informationen</strong> und dann <strong>Trotzdem ausführen</strong> klicken. Der Installer ist noch nicht von Microsoft signiert; das holen wir nach.</>,
  en: <>On first launch Windows shows “Windows protected your PC” — click <strong>More info</strong>, then <strong>Run anyway</strong>. The installer isn’t Microsoft-signed yet; we’re working on it.</>,
};

// Bewusst ohne Symbol: der Apfel und der Monitor sahen daneben aus. Der Text
// sagt alles, was der Knopf sagen muss.
function Knopf({ href, children, testid }) {
  return (
    <a href={href} data-testid={testid}
       className="inline-flex items-center justify-center px-6 py-3 rounded-full font-manrope font-bold text-sm text-black transition-all duration-300 hover:scale-[1.03]"
       style={{ background: 'var(--primary)', boxShadow: '0 0 24px rgba(249,115,22,0.25)' }}>
      {children}
    </a>
  );
}

// kompakt: Kachel im Dashboard. Sonst: voller Block auf der Produktseite.
export default function InstallerDownload({ isEnglish = false, kompakt = false, zxpHinweis = true }) {
  const info = useInstaller();
  if (!info) return null;
  const sp = isEnglish ? 'en' : 'de';
  const t = {
    de: { kicker: 'So geht’s los', titel: 'David Media Installer', text: 'Gratis für alle: Mit deinem Konto anmelden, und der Installer richtet alles ein, was du besitzt — Einzelkauf, Studio Flow, Abo oder Gratis-Panel. Installiert die Erweiterungen und hält sie aktuell.',
          mac: 'Für Mac', win: 'Für Windows', zxp: 'Nur die Datei? Jede Erweiterung gibt es nach dem Kauf auch einzeln als .zxp.' },
    en: { kicker: 'Getting started', titel: 'David Media Installer', text: 'Free for everyone: sign in with your account and the installer sets up everything you own — single purchase, Studio Flow, subscription or free panel. Installs the extensions and keeps them up to date.',
          mac: 'For Mac', win: 'For Windows', zxp: 'Just the file? Every extension is also available as a single .zxp after purchase.' },
  }[sp];

  return (
    <div className={`rounded-2xl liquid-glass ${kompakt ? 'p-5' : 'p-6 md:p-8'}`} data-testid="installer-download">
      <div className={`flex ${kompakt ? 'flex-col gap-4' : 'flex-col md:flex-row md:items-center gap-6'}`}>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--primary-text, #FF9D5C)' }}>{t.kicker}</p>
          <h3 className={`font-syne font-bold text-white ${kompakt ? 'text-lg' : 'text-2xl'} leading-tight`}>
            {t.titel} <span className="font-mono text-xs font-normal text-white/50 ml-1">v{info.version}</span>
          </h3>
          <p className="font-manrope text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{t.text}</p>
        </div>
        <div className={`flex ${kompakt ? 'flex-wrap' : 'flex-col sm:flex-row md:flex-col'} gap-3 shrink-0`}>
          <Knopf href={info.url} testid="installer-mac">{t.mac}</Knopf>
          {info.url_win && <Knopf href={info.url_win} testid="installer-win">{t.win}</Knopf>}
        </div>
      </div>
      {zxpHinweis && (
        <p className="flex items-start gap-2 font-manrope text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
          <Download className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
          <span>{t.zxp}</span>
        </p>
      )}
    </div>
  );
}

// Ein .zxp-Link, der erst fragt: "Lieber den Installer?" Der Installer ist
// der Weg, auf dem Kunden bleiben (Updates, alles an einem Ort) — die nackte
// Datei ist die Ausnahme fuer die, die es ausdruecklich wollen. Deshalb
// steht der Installer vorne und die Datei als zweite Wahl darunter.
export function ZxpLink({ href, isEnglish = false, className = '', style, children, testid }) {
  const [offen, setOffen] = useState(false);
  const info = useInstaller();
  const sp = isEnglish ? 'en' : 'de';
  const t = {
    de: { titel: 'Lieber den Installer?', text: 'Der David Media Installer richtet alles ein und hält es aktuell — die .zxp musst du selbst installieren und bekommst keine Updates.',
          mac: 'Installer für Mac', win: 'Installer für Windows', zxp: 'Nur die .zxp laden', zu: 'Schließen' },
    en: { titel: 'Prefer the installer?', text: 'The David Media Installer sets everything up and keeps it current — the .zxp you install yourself, without updates.',
          mac: 'Installer for Mac', win: 'Installer for Windows', zxp: 'Just download the .zxp', zu: 'Close' },
  }[sp];

  // Ohne bekannte Installer-Adresse gibt es nichts zu fragen — dann ganz normal laden.
  const fragen = (e) => { if (!info) return; e.preventDefault(); setOffen(true); };

  return (
    <>
      <a href={href} onClick={fragen} className={className} style={style} data-testid={testid}>{children}</a>
      {offen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }}
             onClick={() => setOffen(false)} data-testid="zxp-frage">
          <div className="w-full max-w-md rounded-2xl liquid-glass p-6" style={{ background: 'rgba(14,14,16,0.96)' }} onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--primary-text, #FF9D5C)' }}>David Media Installer</p>
            <h3 className="font-syne font-bold text-xl text-white">{t.titel}</h3>
            <p className="font-manrope text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{t.text}</p>
            <div className="flex flex-col gap-2 mt-5">
              <Knopf href={info.url} testid="zxp-frage-mac">{t.mac}</Knopf>
              {info.url_win && <Knopf href={info.url_win} testid="zxp-frage-win">{t.win}</Knopf>}
              <a href={href} data-testid="zxp-frage-zxp"
                 className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-manrope text-sm text-white/70 hover:text-white transition"
                 style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                 onClick={() => setOffen(false)}>
                <Download className="w-4 h-4" /> {t.zxp}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
